import {extendTheme} from '@chakra-ui/react';

const colors = {
    primaryGreen: '#00A650',
    primaryRed: '#C60F0F',
    white: '#FFFFFF',
    yellow: '#FFE500',
    orange: '#c96835',
    white80: 'rgba(255,255,255,0.8)',
    black: '#000000',
    eventCard: '#7B9266',
    greyHomeCard: '#3C4042',
    greyHomeCardForeground: '#4c5254',
    greySkeletonForeground: '#545b5d',
    greySkeletonBackground: '#252528',
    chatbotBlue: '#007BA7',
    primary: {
        50: '#f8faf6',
        100: '#e1e3df',
        200: '#c1e9fb',
        300: '#8af8c5',
        400: '#006c4b',
        500: '#00724f',
        600: '#006c4b',
        700: '#005f41',
        800: '#005137',
        900: '#003825',
    },
    secondary: {
        50: '#ffffff',
        100: '#ffffff',
        200: '#cfe9d9',
        300: '#4d6357',
        400: '#cfe9d9',
        500: '#b4ccbd',
        600: '#b4ccbd',
        700: '#a5ccde',
        800: '#244c5a',
        900: '#073543',
    },
    tertiary: {
        50: '#ffffff',
        100: '#c7eafa',
        200: '#c1e9fb',
        300: '#3d6373',
        400: '#c1e9fb',
        500: '#a5ccde',
        600: '#a5ccde',
        700: '#244c5a',
        800: '#001f29',
        900: '#001f29',
    },
    error: {
        50: '#ffdad6',
        100: '#ffdad6',
        200: '#ffdad6',
        300: '#ffdad6',
        400: '#ffb4ab',
        500: '#ffb4ab',
        600: '#ffb4ab',
        700: '#93000a',
        800: '#690005',
        900: '#690005',
    },
    onBackground: '#191c1a',
    surface: {
        light: "#f5fbf5",
        dark: "#111412",
    },
    onSurface: {
        light: "#191c1a",
        dark: "#c5c7c3",
    },
    onPrimary: {
        light: "#ffffff",
        dark: "#003825",
    },
    onSecondary: {
        light: "#ffffff",
        dark: "#1f352a",
    },
    onTertiary: {
        light: "#ffffff",
        dark: "#073543",
    },
    onNeutral: {
        light: "#191c1a",
        dark: "#e1e3df",
    },
    onError: {
        light: "#ffffff",
        dark: "#690005",
    },
    background: {
        light: "#fbfdf9",
        dark: "#191c1a",
    },
    outline: {
        light: "#707973",
        dark: "#8a938c",
    },
    inverseOnSurface: {
        light: "#eff1ed",
        dark: "#191c1a",
    },
    inverseSurface: {
        light: "#2e312f",
        dark: "#e1e3df",
    },
    inversePrimary: {
        light: "#6ddbaa",
        dark: "#006c4b",
    },
    shadow: {
        light: "#000000",
        dark: "#000000",
        image: "rgba(0,0,0,0.2)"
    },
    surfaceTint: {
        light: "#006c4b",
        dark: "#6ddbaa",
    },
    outlineVariant: {
        light: "#bfc9c1",
        dark: "#404943",
    },
    scrim: {
        light: "#000000",
        dark: "#000000",
    },
    surfaceVariant: {
        light: "#dce5dd",
        dark: "#404943",
    },
    onSurfaceVariant: {
        light: "#404943",
        dark: "#bfc9c1",
    },
    surfaceContainerHighest: {
        light: "#e1e3df",
        dark: "#323633",
    },
    surfaceContainerHigh: {
        light: "#e7e9e5",
        dark: "#272b28",
    },
    surfaceContainer: {
        light: "#eceeea",
        dark: "#1d201e",
    },
    surfaceContainerLow: {
        light: "#f2f4f0",
        dark: "#191c1a",
    },
    surfaceContainerLowest: {
        light: "#ffffff",
        dark: "#0c0f0d",
    },
    surfaceDim: {
        light: "#d8dbd7",
        dark: "#111412",
    },
    surfaceBright: {
        light: "#f8faf6",
        dark: "#373a37",
    }
}
const fonts = {
    body: 'Roboto, sans-serif',
    heading: 'Roboto, sans-serif',
    mono: 'Menlo, monospace',
}
const fontSizes = {
    large: '2.25rem',
    medium: '1.75rem',
    small: '1.125rem',
    ...Array.from({length: 150}, (_, i) => i + 1).reduce((acc, curr) => {
        // @ts-ignore
        acc[`$${curr}`] = curr;
        return acc;
    }, {}),
}
const size = {
    true: 30,
    display: 48,
    h1: 32,
    h2: 28,
    h3: 24,
    h4: 20,
    h5: 16,
    bodyL: 16,
    bodyM: 14,
    bodyS: 12,
    ...Array.from({length: 150}, (_, i) => i + 1).reduce((acc, curr) => {
        // @ts-ignore
        acc[`$${curr}`] = curr;
        return acc;
    }, {}),
}
const fontWeights = {
    regular: 400,
    medium: 500,
}
const lineHeights = {
    large: '2.5rem',
    medium: '2rem',
    small: '1.75rem',
}
const letterSpacings = {
    normal: 'normal',
}
const components = {
    Button: {
        variants: {
            primary: {
                bg: 'primary.400',
                color: 'white',
                w: '100%',
                h: "48px",
                borderRadius: 100,
                fontWeight: 400
            },
            tertiary: {
                bg: 'tertiary.300',
                color: 'white',
                w: '100%',
                h: "48px",
                borderRadius: 100,
                fontWeight: 400
            }
        },
        default: {
            fontSize: '16px',
            fontWeight: 400,
            paddingY: '16px',
        }
    }
}

export const materialTheme = extendTheme({
    colors,
    fonts,
    fontSizes,
    size,
    fontWeights,
    lineHeights,
    letterSpacings,
    components,
    styles: {
        global: {
            ':root': {
                '--chakra-ui-unit': 'px'
            }
        }
    }
});

export function scrollbarCss({marginLeft = 0, marginRight = 0}: { marginLeft?: number, marginRight?: number }) {
    return {
        "&::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
        },
        "&::-webkit-scrollbar-track": {
            height: "6px",
            marginBlock: 16,
            marginLeft: marginLeft,
            marginRight: marginRight
        },
        "&::-webkit-scrollbar-thumb": {
            background: "#3d6373",
            borderRadius: "24px",
            paddingLeft: "16px",
            backgroundClip: "padding-box",
            marginStart: "8px",
        }
    }
}
